import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { whatsappHref } from "@/lib/contact";

export const UNITS = [
  {
    id: "block-a-office",
    name: "Block A — IT/ITES Office Space",
    type: "Office",
    category: "office",
    channel: "Resale",
    ready: true,
    rent: "₹55–65 /sq.ft · mo",
    status: "Phase I · Block A",
    spec: "Floors 3–22 · 22 lifts",
    rera: "UPRERAPRJ211016",
    size: "800 – 2,400 sq.ft",
    price: "From ₹64 L",
    img: "https://www.fairfoxitinfra.com/public/media/images/vertical/office-1-1718104631.jpg",
  },
  {
    id: "gf-retail",
    name: "Ground Floor — Double-Height Retail",
    type: "Retail",
    category: "retail",
    channel: "Resale",
    ready: true,
    rent: "₹120–150 /sq.ft · mo",
    status: "Phase I · Block A",
    spec: "Ground & first floor frontage",
    rera: "UPRERAPRJ211016",
    size: "300 – 1,200 sq.ft",
    price: "From ₹72 L",
    img: "https://www.fairfoxitinfra.com/public/media/images/vertical/retail-1-1718104715.jpg",
  },
  {
    id: "food-court",
    name: "Second Floor — Food Court Space",
    type: "Food Court",
    category: "food",
    channel: "Primary",
    ready: true,
    rent: "₹100–130 /sq.ft · mo",
    status: "Phase I · Block A",
    spec: "Dedicated dining level",
    rera: "UPRERAPRJ211016",
    size: "250 – 600 sq.ft",
    price: "From ₹60 L",
    img: "https://www.fairfoxitinfra.com/public/media/images/certificate/eon-noida-11-food-court-hr-1710831733.png",
  },
  {
    id: "block-e-office",
    name: "Block E — IT/ITES Office Space",
    type: "Office",
    category: "office",
    channel: "Primary",
    ready: false,
    rent: null,
    status: "Phase II · Block E",
    spec: "Floors 3–15 · 22 lifts",
    rera: "UPRERAPRJ716606",
    size: "700 – 2,000 sq.ft",
    price: "From ₹56 L",
    img: "https://www.fairfoxitinfra.com/public/media/images/vertical/eon_noida_022-1712903433-1718104553.jpeg",
  },
  {
    id: "block-d-suites",
    name: "Block D — Office Suites",
    type: "Office Suite",
    category: "suites",
    channel: "Primary",
    ready: false,
    rent: null,
    status: "Phase III · Block D",
    spec: "Floors 3–9 · suites format",
    rera: "UPRERAPRJ882013",
    size: "400 – 900 sq.ft",
    price: "From ₹32 L",
    img: "https://www.fairfoxitinfra.com/public/media/images/certificate/eon-noida-03-atrium-1710830821.png",
  },
  {
    id: "block-bd-retail",
    name: "Blocks B & D — Retail Frontage",
    type: "Retail",
    category: "retail",
    channel: "Primary",
    ready: false,
    rent: null,
    status: "Phase III · Blocks B & D",
    spec: "Ground & first floor retail",
    rera: "UPRERAPRJ882013",
    size: "350 – 1,000 sq.ft",
    price: "From ₹84 L",
    img: "https://www.fairfoxitinfra.com/public/media/images/vertical/retail-2-1718104696.jpg",
  },
];

const CHANNEL_FILTERS = [
  { key: "all", label: "All" },
  { key: "Primary", label: "Primary" },
  { key: "Resale", label: "Resale" },
];

const TYPE_FILTERS = [
  { key: "all", label: "All Types" },
  { key: "office", label: "Office" },
  { key: "retail", label: "Retail" },
  { key: "food", label: "Food Court" },
  { key: "suites", label: "Office Suites" },
];

const pill = (active) =>
  `px-5 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
    active
      ? "bg-[#D4AF37] text-[#0B0C0E] shadow-[0_0_20px_rgba(212,175,55,0.35)]"
      : "border border-white/10 text-[#9CA3AF] hover:border-[#D4AF37]/50 hover:text-[#F3F4F6]"
  }`;

const unitWa = (u) =>
  whatsappHref(`Hi Ashish, I want details on ${u.name} — ${u.channel}, ${u.size}, ${u.price} (EON Fairfox).`);

export default function Opportunities({ channel, setChannel }) {
  const [typeFilter, setTypeFilter] = useState("all");
  const units = UNITS.filter(
    (u) =>
      (channel === "all" || u.channel === channel) &&
      (typeFilter === "all" || u.category === typeFilter)
  );

  return (
    <section id="opportunities" data-testid="opportunities-section" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Investment Opportunities</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]">
            Find your EON <span className="gold-text italic">opportunity.</span>
          </h2>
          <div className="mt-8 flex flex-col gap-3" data-testid="opportunity-filters">
            <div className="flex flex-wrap gap-2" data-testid="channel-filters">
              {CHANNEL_FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`filter-channel-${f.key.toLowerCase()}`}
                  onClick={() => setChannel(f.key)}
                  className={pill(channel === f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="type-filters">
              {TYPE_FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`filter-type-${f.key}`}
                  onClick={() => setTypeFilter(f.key)}
                  className={pill(typeFilter === f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div layout className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {units.map((u) => (
              <motion.article
                layout
                key={u.id}
                data-testid={`unit-card-${u.id}`}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="group relative rounded-2xl overflow-hidden border border-white/[0.07] bg-[#121418] hover:border-[#D4AF37]/40 transition-colors duration-500"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={u.img}
                    alt={u.name}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121418] via-transparent to-transparent" />
                  <span
                    data-testid={`unit-status-${u.id}`}
                    className="absolute top-4 left-4 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] backdrop-blur-md bg-[#0B0C0E]/70 text-[#F3E5AB] border border-[#D4AF37]/40"
                  >
                    {u.status}
                  </span>
                  {u.ready && (
                    <span
                      data-testid={`unit-ready-${u.id}`}
                      className="absolute top-4 right-4 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] bg-[#D4AF37] text-[#0B0C0E]"
                    >
                      Ready to Move
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{u.type}</span>
                    <span className="text-[#4B5563]">·</span>
                    <span
                      data-testid={`unit-channel-${u.id}`}
                      className={`font-mono text-[10px] uppercase tracking-[0.25em] ${u.channel === "Resale" ? "text-[#F3E5AB]" : "text-[#9CA3AF]"}`}
                    >
                      {u.channel}
                    </span>
                  </div>
                  <h3 className="mt-2 font-display text-xl font-semibold text-[#F3F4F6] leading-snug">{u.name}</h3>
                  <div className="mt-2 text-xs text-[#9CA3AF]">{u.spec}</div>
                  {u.rent && (
                    <div className="mt-2 font-mono text-[11px] text-[#9CA3AF]">
                      Indicative rent: <span className="text-[#F3E5AB]">{u.rent}</span>
                    </div>
                  )}
                  <div className="mt-4 flex items-end justify-between border-t border-white/[0.07] pt-4">
                    <div>
                      <div className="font-mono text-sm font-medium text-[#F3F4F6]">{u.size}</div>
                      <div className="mt-0.5 text-[11px] text-[#9CA3AF]">RERA: {u.rera}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-medium gold-text">{u.price}</div>
                      <div className="mt-0.5 text-[11px] text-[#9CA3AF]">all-inclusive*</div>
                    </div>
                  </div>
                  <a
                    href={unitWa(u)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`details-btn-${u.id}`}
                    className="mt-5 w-full py-3 rounded-full border border-[#D4AF37]/40 font-sans text-xs font-semibold uppercase tracking-[0.18em] text-[#F3F4F6] hover:bg-[#D4AF37] hover:text-[#0B0C0E] hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Get Details on WhatsApp
                    <ArrowUpRight size={14} />
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <p data-testid="pricing-footnote" className="mt-8 text-[11px] text-[#6B7280] leading-relaxed max-w-2xl">
          *Indicative &quot;from&quot; pricing — offices and suites at all-inclusive rates of ₹8,000 – ₹9,500 per sq.ft;
          retail and food-court spaces from ₹24,000 per sq.ft, varying by floor, facing and size. Subject to
          availability and prevailing pricing — verify current inventory with the Homewiz.in advisory team.
        </p>
      </div>
    </section>
  );
}
